import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Form,
  Table,
  InputGroup,
  FormControl,
  Pagination,
  Badge,
  Button,
} from "react-bootstrap";
import { getLevelChipClass } from "../utils/levelColors";

const API_URL = process.env.REACT_APP_API_URL;

const levelMap = {
  8: "H",
  7: "G",
  1: "F",
  2: "E",
  3: "D",
  4: "C",
  5: "B",
  6: "A",
};

const getLevelCharFromId = (id) => {
  return levelMap[id] || `ID ${id}`;
};

const PlayerStats = () => {
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [levelFilter, setLevelFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [playersPerPage] = useState(20);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const getProposedLevel = useCallback((currentLevelId, accumulatedPoints) => {
    const currentLevelChar = getLevelCharFromId(currentLevelId);
    const levels = ["H", "G", "F", "E", "D", "C", "B", "A"];
    const currentIndex = levels.indexOf(currentLevelChar);

    if (currentIndex === -1) return currentLevelChar;

    if (accumulatedPoints >= 100) {
      if (currentIndex < levels.length - 1) {
        return levels[currentIndex + 1];
      }
    } else if (accumulatedPoints <= -100) {
      if (currentIndex > 0) {
        return levels[currentIndex - 1];
      }
    }
    return "";
  }, []);

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const response = await axios.get(`${API_URL}/seasons`);
        setSeasons(response.data);
      } catch (err) {
        console.error("Error fetching seasons:", err);
        setError("Failed to load seasons.");
      } finally {
        setLoading(false);
      }
    };
    fetchSeasons();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSeason, debouncedSearchTerm, levelFilter, sortColumn, sortDirection]);

  useEffect(() => {
    const fetchPlayers = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: playersPerPage,
          ...(selectedSeason ? { season_id: selectedSeason } : {}),
          search: debouncedSearchTerm,
          level: levelFilter,
          sortColumn: sortColumn,
          sortDirection: sortDirection,
        };
        const response = await axios.get(`${API_URL}/playerspublic`, { params });
        setPlayers(response.data);

        const countParams = {
          ...(selectedSeason ? { season_id: selectedSeason } : {}),
          search: debouncedSearchTerm,
          level: levelFilter,
        };
        const countResponse = await axios.get(`${API_URL}/playerspublic/count`, {
          params: countParams,
        });
        setTotalPlayers(countResponse.data.total);
      } catch (err) {
        console.error("Error fetching players:", err);
        setError("Không thể tải dữ liệu Vận Động Viên.");
      } finally {
        setLoading(false);
      }
    };
    fetchPlayers();
  }, [
    selectedSeason,
    debouncedSearchTerm,
    levelFilter,
    currentPage,
    playersPerPage,
    sortColumn,
    sortDirection,
  ]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const renderPaginationItems = () => {
    const totalPages = Math.ceil(totalPlayers / playersPerPage);
    const pageNumbers = [];
    const maxPageButtons = 5;

    if (totalPages <= maxPageButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
      let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

      if (endPage - startPage + 1 < maxPageButtons) {
        startPage = Math.max(1, endPage - maxPageButtons + 1);
      }

      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) {
          pageNumbers.push("ellipsis-start");
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pageNumbers.push("ellipsis-end");
        }
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers.map((page) => {
      if (page === "ellipsis-start") {
        return <Pagination.Ellipsis key="ellipsis-start" disabled />;
      }
      if (page === "ellipsis-end") {
        return <Pagination.Ellipsis key="ellipsis-end" disabled />;
      }
      return (
        <Pagination.Item
          key={`page-${page}`}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    });
  };

  if (loading && !selectedSeason) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Đang tải...</span>
        </Spinner>
        <p className="mt-3">Đang tải danh sách mùa giải...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger" className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
          {error}
        </Alert>
      </Container>
    );
  }

  const totalPages = Math.ceil(totalPlayers / playersPerPage);
  const selectedSeasonName =
    seasons.find((s) => s.id === parseInt(selectedSeason))?.name || "Tất cả mùa giải";

  return (
    <Container className="py-4">
      <h1 className="mb-4 fw-bold">
        Thống Kê Vận Động Viên
      </h1>

      {/* Header Statistics */}
      <Row className="mb-4 g-3">
        <Col md={4} xs={12}>
          <Card className="text-start border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-4 px-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Tổng VĐV</p>
              <h2 className="fw-bold mb-1">
                {totalPlayers}
              </h2>
              <small className="text-muted">{selectedSeasonName}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} xs={12}>
          <Card className="text-start border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-4 px-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Đang Hiển Thị</p>
              <h2 className="fw-bold mb-1">
                {players.length}
              </h2>
              <small className="text-muted">Trang {currentPage}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} xs={12}>
          <Card className="text-start border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-4 px-4">
              <p className="text-muted small text-uppercase fw-bold mb-2">Phân Trang</p>
              <h2 className="fw-bold mb-1 text-dark">
                {totalPages}
              </h2>
              <small className="text-muted">{playersPerPage} VĐV / Trang</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
        <Card.Body className="p-4">
          <Row className="g-3">
            <Col md={4} xs={12}>
              <Form.Group>
                <Form.Label className="text-muted small text-uppercase fw-bold mb-2">
                  Mùa Giải
                </Form.Label>
                <Form.Select
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(e.target.value)}
                  style={{ borderRadius: '8px' }}
                >
                  <option value="">Tất cả mùa giải</option>
                  {seasons.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} xs={12}>
              <Form.Group>
                <Form.Label className="text-muted small text-uppercase fw-bold mb-2">
                  Cấp Độ
                </Form.Label>
                <Form.Select
                  value={levelFilter}
                  onChange={(e) => setLevelFilter(e.target.value)}
                  style={{ borderRadius: '8px' }}
                >
                  <option value="">Tất cả cấp độ</option>
                  {Object.entries(levelMap).map(([id, char]) => (
                    <option key={id} value={id}>
                      Hạng {char}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} xs={12}>
              <Form.Group>
                <Form.Label className="text-muted small text-uppercase fw-bold mb-2">
                  Tìm Kiếm
                </Form.Label>
                <InputGroup>
                  <FormControl
                    placeholder="Tìm theo tên VĐV..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ borderRadius: '8px 0 0 8px' }}
                  />
                  {searchTerm && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setSearchTerm("")}
                      style={{ borderRadius: '0 8px 8px 0' }}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Players Table */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" className="mb-3" />
              <p>Đang tải danh sách vận động viên...</p>
            </div>
          ) : players.length > 0 ? (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="bg-white border-bottom">
                    <tr>
                      <th className="text-center" style={{ width: "60px" }}>
                        #
                      </th>
                      <th style={{ width: "25%" }}>Tên VĐV</th>
                      <th style={{ width: "25%" }}>Đội</th>
                      <th className="text-center" style={{ width: "12%" }}>
                        Cấp Độ
                      </th>
                      <th className="text-center" style={{ width: "12%" }}>
                        Đề Xuất
                      </th>
                      <th
                        className="text-center"
                        style={{ width: "16%", cursor: "pointer" }}
                        onClick={() => handleSort("level_point")}
                      >
                        Điểm Tích Lũy{" "}
                        {sortColumn === "level_point" && (
                          <span style={{ color: '#fff' }}>
                            {sortDirection === "asc" ? "▲" : "▼"}
                          </span>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map((player, index) => {
                      const currentLevel = getLevelCharFromId(player.level_present);
                      const proposedLevel = getProposedLevel(
                        player.level_present,
                        parseFloat(player.level_point),
                      );
                      const levelPoint = parseFloat(player.level_point);

                      return (
                        <tr key={player.id} className="align-middle">
                          <td className="text-center fw-bold text-muted">
                            {(currentPage - 1) * playersPerPage + index + 1}
                          </td>
                          <td>
                            <Link
                              to={`/players/${player.id}`}
                              style={{
                                textDecoration: 'none',
                                fontWeight: '600',
                                color: 'inherit',
                                transition: 'color 0.2s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-color)'}
                              onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                            >
                              {player.name}
                            </Link>
                          </td>
                          <td>
                            <small className="text-muted">
                              {player.team_name || "Chưa có đội"}
                            </small>
                          </td>
                          <td className="text-center">
                            <span className={`${getLevelChipClass(currentLevel)} level-chip-lg`}>
                              {currentLevel}
                            </span>
                          </td>
                          <td className="text-center">
                            {proposedLevel ? (
                              <Badge
                                bg={
                                  levelPoint >= 100
                                    ? "success"
                                    : levelPoint <= -100
                                      ? "danger"
                                      : "secondary"
                                }
                                className="fs-6 px-3 py-2"
                                style={{ minWidth: "50px", borderRadius: '8px' }}
                              >
                                {levelPoint >= 100 ? "↑" : "↓"} {proposedLevel}
                              </Badge>
                            ) : (
                              <small className="text-muted">-</small>
                            )}
                          </td>
                          <td className="text-center">
                            <Badge
                              bg={
                                levelPoint >= 100
                                  ? "success"
                                  : levelPoint <= -100
                                    ? "danger"
                                    : levelPoint >= 50
                                      ? "warning"
                                      : levelPoint <= -50
                                        ? "warning"
                                        : "secondary"
                              }
                              className="fs-6 px-3 py-2"
                              style={{ minWidth: "80px", borderRadius: '8px' }}
                            >
                              {levelPoint.toFixed(2)}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-4 border-top">
                  <small className="text-muted">
                    Hiển thị{" "}
                    <strong className="text-primary">
                      {(currentPage - 1) * playersPerPage + 1}
                    </strong>{" "}
                    đến{" "}
                    <strong className="text-primary">
                      {Math.min(currentPage * playersPerPage, totalPlayers)}
                    </strong>{" "}
                    trong tổng số{" "}
                    <strong className="text-primary">{totalPlayers}</strong> VĐV
                  </small>
                  <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <Alert variant="info" className="m-4 border-0" style={{ borderRadius: '12px' }}>
              {searchTerm
                ? `Không tìm thấy vận động viên nào phù hợp với "${searchTerm}"`
                : "Không tìm thấy vận động viên nào theo tiêu chí đã chọn."}
            </Alert>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PlayerStats;
